import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, FormControl, Grid, MenuItem, Paper, Select, Typography } from '@mui/material';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useSelector } from 'react-redux';
import BasicRangeShortcuts from '../../components/date-range-picker/BasicRangeShortcuts';
import { fetchEmailSummary, fetchEmailTimeseries, fetchEmailEvents } from '../../api/emailTracking';
import EmailEventsTable from './EmailEventsTable';

const selectSx = {
  minWidth: 150,
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: '#f3f4f6',
    '& fieldset': { borderColor: '#d1d5db' },
  },
  '& .MuiSelect-select': { padding: '8px 12px', fontSize: '14px' },
};

// Stat cards rendered from summary.cards. `sub` is the secondary rate line.
const CARD_DEFS = [
  { key: 'totalSent', label: 'Total Sent', color: '#2E80DB' },
  { key: 'delivered', label: 'Delivered', rateKey: 'deliveryRate', color: '#22A06B' },
  { key: 'opened', label: 'Opened', rateKey: 'openRate', color: '#7A5AF8' },
  { key: 'clicked', label: 'Clicked', rateKey: 'clickRate', color: '#F2994A' },
  { key: 'bounced', label: 'Bounced', rateKey: 'bounceRate', color: '#E5484D' },
  { key: 'complained', label: 'Complaints', color: '#9A6700' },
];

const PIE_COLORS = ['#22A06B', '#7A5AF8', '#2E80DB', '#F2994A', '#E5484D', '#9A6700', '#8E8E8E'];

const EmailAnalytics = () => {
  const dateRange = useSelector((state) => state.filter.dateRange);

  const [campaign, setCampaign] = useState('');
  const [eventType, setEventType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [eventsData, setEventsData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const debounceRef = useRef(null);

  // Debounce the recipient search box.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const baseFilter = useMemo(
    () => ({ dateRange, campaign, eventType, search }),
    [JSON.stringify(dateRange), campaign, eventType, search]
  );

  // Summary + trend chart.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingSummary(true);
        const [s, ts] = await Promise.all([
          fetchEmailSummary(baseFilter),
          fetchEmailTimeseries(baseFilter),
        ]);
        if (!active) return;
        setSummary(s);
        setTimeseries(ts || []);
      } catch (err) {
        console.error('Error loading email analytics summary', err);
      } finally {
        if (active) setLoadingSummary(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [JSON.stringify(baseFilter)]);

  // Paginated events table.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoadingEvents(true);
        const data = await fetchEmailEvents({ ...baseFilter, page, limit: 25 });
        if (active) setEventsData(data);
      } catch (err) {
        console.error('Error loading email events', err);
      } finally {
        if (active) setLoadingEvents(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [JSON.stringify(baseFilter), page]);

  const cards = summary?.cards ?? {};
  const campaigns = summary?.filters?.campaigns ?? [];
  const eventTypes = summary?.filters?.eventTypes ?? [];

  const trendOptions = useMemo(
    () => ({
      chart: { type: 'areaspline', height: 320 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: { categories: timeseries.map((d) => d.date), tickInterval: Math.ceil(timeseries.length / 12) || 1 },
      yAxis: { title: { text: 'Emails' }, allowDecimals: false },
      legend: { align: 'center', verticalAlign: 'bottom' },
      plotOptions: { areaspline: { fillOpacity: 0.15, marker: { enabled: false } } },
      series: [
        { name: 'Sent', data: timeseries.map((d) => d.sent), color: '#2E80DB' },
        { name: 'Delivered', data: timeseries.map((d) => d.delivered), color: '#22A06B' },
        { name: 'Opened', data: timeseries.map((d) => d.opened), color: '#7A5AF8' },
        { name: 'Bounced', data: timeseries.map((d) => d.bounced), color: '#E5484D' },
      ],
    }),
    [timeseries]
  );

  const breakdownOptions = useMemo(
    () => ({
      chart: { type: 'pie', height: 320 },
      title: { text: null },
      credits: { enabled: false },
      tooltip: { pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)' },
      plotOptions: {
        pie: {
          innerSize: '55%',
          dataLabels: { enabled: true, format: '{point.name}: {point.y}' },
        },
      },
      series: [
        {
          name: 'Events',
          colorByPoint: true,
          colors: PIE_COLORS,
          data: (summary?.breakdown ?? []).map((b) => ({ name: b.type, y: b.count })),
        },
      ],
    }),
    [summary]
  );

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000' }}>
          Email Analytics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              name="search"
              placeholder="Search recipient"
              value={searchInput}
              autoComplete="off"
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <span className="icon">
              <iconify-icon icon="ion:search-outline" />
            </span>
          </form>

          <FormControl size="small" sx={selectSx}>
            <Select
              value={campaign}
              displayEmpty
              IconComponent={KeyboardArrowDownOutlinedIcon}
              onChange={(e) => {
                setCampaign(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Campaigns</MenuItem>
              {campaigns.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: '14px' }}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={selectSx}>
            <Select
              value={eventType}
              displayEmpty
              IconComponent={KeyboardArrowDownOutlinedIcon}
              onChange={(e) => {
                setEventType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Events</MenuItem>
              {eventTypes.map((t) => (
                <MenuItem key={t} value={t} sx={{ fontSize: '14px' }}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <BasicRangeShortcuts id="emailRange" />
        </Box>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} py={2}>
        {CARD_DEFS.map((def) => (
          <Grid item xs={6} sm={4} md={2} key={def.key}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                p: 2,
                minHeight: 110,
                border: '1px solid #E8E8E8',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#3C4043' }}>
                {def.label}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#000', fontSize: '22px' }}>
                {loadingSummary ? '—' : (cards[def.key] ?? 0).toLocaleString()}
              </Typography>
              {def.rateKey && (
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: def.color }}>
                  {loadingSummary ? '' : `${cards[def.rateKey] ?? 0}%`}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <div className="border rounded-4 p-20">
            <h6 className="text-lg mb-16">Delivery Trend</h6>
            <HighchartsReact highcharts={Highcharts} options={trendOptions} />
          </div>
        </Grid>
        <Grid item xs={12} md={5}>
          <div className="border rounded-4 p-20">
            <h6 className="text-lg mb-16">Event Breakdown</h6>
            <HighchartsReact highcharts={Highcharts} options={breakdownOptions} />
          </div>
        </Grid>
      </Grid>

      {/* Top templates */}
      <div className="border rounded-4 p-20 mt-24">
        <h6 className="text-lg mb-16">Top Templates</h6>
        <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <table className="table bordered-table sm-table mb-0" style={{ position: 'relative' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                <th>#</th>
                <th>Subject / Template</th>
                <th className="text-center">Sent</th>
                <th className="text-center">Delivered</th>
                <th className="text-center">Delivery Rate</th>
              </tr>
            </thead>
            <tbody>
              {loadingSummary ? (
                <tr>
                  <td colSpan="5" className="text-center py-3">Loading…</td>
                </tr>
              ) : summary?.topTemplates?.length ? (
                summary.topTemplates.map((t, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{t.template}</td>
                    <td className="text-center">{t.total.toLocaleString()}</td>
                    <td className="text-center">{t.delivered.toLocaleString()}</td>
                    <td className="text-center">{t.deliveryRate}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-3">No templates found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Events table */}
      <EmailEventsTable
        events={eventsData?.events}
        loading={loadingEvents}
        page={page}
        totalPages={eventsData?.totalPages ?? 0}
        onPageChange={(_, p) => setPage(p)}
      />
    </div>
  );
};

export default EmailAnalytics;
